/**
 * Property card "Vraag info aan" → scroll to on-page contact (#main-contact) and
 * prefill property reference (title) into ClickDimensions and/or native contact form.
 */
import {
	getClickDimensionsSearchScopes,
	getClickDimensionsSurveyModel,
} from './clickdimensions-hidden-fields.js';

const STORAGE_KEY = 'regatta_property_inquiry_ref';

/** @type {ReturnType<typeof window.setInterval> | null} */
let cdRetryTimer = null;

function getConfig() {
	return window.regattaPropertyInquiry || {};
}

function getContactSection() {
	const id = (getConfig().contactSectionId || 'main-contact').replace(/^#/, '');
	return document.getElementById(id);
}

/** @returns {HTMLElement|null} */
function getOpenModalOverlay() {
	const overlay = document.querySelector('.modal-overlay.is-open');
	return overlay instanceof HTMLElement ? overlay : null;
}

/** Scope for CD prefill: open modal first (even before CD mounts), else on-page contact. */
function getPropertyFormScope() {
	const modal = getOpenModalOverlay();
	if (modal) {
		const mainColumn = modal.querySelector('.modal-column--main');
		if (mainColumn) {
			return mainColumn;
		}

		const formRoot = modal.querySelector(
			'.modal-body__form, .modal-event__form, .regatta-cd-form'
		);
		if (formRoot) {
			return (
				formRoot.closest('.modal-column--main') ||
				formRoot.closest('.modal-event__main') ||
				formRoot.closest('.modal-body') ||
				formRoot
			);
		}
		const main = modal.querySelector('.modal-event__main, .modal-body');
		if (main) {
			return main;
		}
		return modal;
	}
	return getContactSection() || document;
}

function isPlanDownloadPending() {
	try {
		return Boolean(sessionStorage.getItem(STORAGE_KEY)) && getOpenModalOverlay();
	} catch {
		return false;
	}
}

function getCdFieldNames() {
	const config = getConfig();
	const names = [];
	const primary = (config.cdFieldName || '').trim();
	if (primary) {
		names.push(primary);
	}
	const extra = config.cdFieldNames;
	if (Array.isArray(extra)) {
		for (const name of extra) {
			const trimmed = String(name || '').trim();
			if (trimmed && !names.includes(trimmed)) {
				names.push(trimmed);
			}
		}
	}
	return names;
}

function getPendingReference() {
	try {
		return sessionStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
}

function clearPendingReference() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		/* ignore */
	}
}

function setNativePropertyField(ref) {
	const el = document.querySelector('#contact-property-reference');
	if (el && 'value' in el) {
		el.value = ref;
	}
}

function domSelectorsForFieldName(fieldName) {
	const raw = String(fieldName || '').trim();
	if (!raw) {
		return [];
	}
	const slug = raw.toLowerCase();
	const names = slug === raw ? [slug] : [slug, raw];
	const selectors = [];
	for (const name of names) {
		selectors.push(
			`[data-name="${name}"] input`,
			`[data-name="${name}"] textarea`,
			`[data-name="${name}"] [contenteditable="true"]`,
			`[data-question-name="${name}"] input`,
			`[data-question-name="${name}"] textarea`,
			`input[name="${name}"]`,
			`textarea[name="${name}"]`
		);
	}
	return selectors;
}

function trySetClickDimensionsFieldDom(ref, section) {
	if (!section || !ref) {
		return false;
	}
	const selectors = getCdFieldNames().flatMap(domSelectorsForFieldName);
	for (const searchScope of getClickDimensionsSearchScopes(section)) {
		for (const selector of selectors) {
			const el = searchScope.querySelector(selector);
			if (el && 'value' in el) {
				el.value = ref;
				el.dispatchEvent(new Event('input', { bubbles: true }));
				el.dispatchEvent(new Event('change', { bubbles: true }));
				return true;
			}
		}
	}
	return false;
}

function trySetClickDimensionsFieldByQuestion(model, ref, fieldNames) {
	if (!model || typeof model.getAllQuestions !== 'function') {
		return false;
	}

	const needles = fieldNames.map((n) => String(n).trim().toLowerCase()).filter(Boolean);
	if (!needles.length) {
		return false;
	}

	for (const question of model.getAllQuestions()) {
		const name = question?.name ? String(question.name) : '';
		const title = question?.title ? String(question.title).toLowerCase() : '';
		const nameLower = name.toLowerCase();
		const matches = needles.some(
			(needle) => needle === nameLower || title === needle || title.includes(needle)
		);

		if (!matches || !name) {
			continue;
		}

		try {
			model.setValue(name, ref);
			return true;
		} catch {
			/* try next question */
		}
	}

	return false;
}

function trySetClickDimensionsField(ref) {
	const fieldNames = getCdFieldNames();
	if (!fieldNames.length || !ref) {
		return false;
	}

	const section = getPropertyFormScope();
	const model = getClickDimensionsSurveyModel(section);
	if (model) {
		for (const fieldName of fieldNames) {
			try {
				model.setValue(fieldName, ref);
				return true;
			} catch {
				/* try next key */
			}
		}

		if (trySetClickDimensionsFieldByQuestion(model, ref, fieldNames)) {
			return true;
		}
	}

	return trySetClickDimensionsFieldDom(ref, section);
}

/**
 * Apply stored reference to DOM + CD form; retries while CD mounts after hash navigation.
 */
export function applyPendingPropertyReference() {
	const ref = getPendingReference();
	if (!ref) {
		return;
	}

	setNativePropertyField(ref);

	if (!getCdFieldNames().length) {
		clearPendingReference();
		return;
	}

	if (trySetClickDimensionsField(ref)) {
		clearPendingReference();
		return;
	}

	if (cdRetryTimer !== null) {
		return;
	}

	let attempts = 0;
	const maxAttempts = isPlanDownloadPending() ? 120 : 60;
	cdRetryTimer = window.setInterval(() => {
		attempts += 1;
		if (trySetClickDimensionsField(ref)) {
			window.clearInterval(cdRetryTimer);
			cdRetryTimer = null;
			clearPendingReference();
		} else if (attempts >= maxAttempts) {
			window.clearInterval(cdRetryTimer);
			cdRetryTimer = null;
			if (!getOpenModalOverlay()) {
				clearPendingReference();
			}
		}
	}, 100);
}

function readReferenceFromLink(anchor) {
	const fromData = anchor.dataset?.propertyReference;
	if (fromData) {
		return fromData.trim();
	}
	const card = anchor.closest('.vzp-facetwp-card, .block-property-offering-card');
	const titleEl = card?.querySelector('.property-card__title');
	if (titleEl) {
		return titleEl.textContent.replace(/\s+/g, ' ').trim();
	}

	const row = anchor.closest('.vzp-facetwp-properties-table__row');
	const tableTitle = row?.querySelector('.vzp-facetwp-properties-table__project');
	if (tableTitle) {
		return tableTitle.textContent.replace(/\s+/g, ' ').trim();
	}

	return '';
}

function storePropertyReference(ref) {
	if (!ref) {
		return;
	}
	try {
		sessionStorage.setItem(STORAGE_KEY, ref);
	} catch {
		/* private mode etc. */
	}
}

function handlePlanDownloadClick(planAnchor) {
	const ref = readReferenceFromLink(planAnchor);
	storePropertyReference(ref);

	// Modal opens on the same click; prefill as soon as the form is in the DOM.
	window.requestAnimationFrame(() => {
		applyPendingPropertyReference();
	});
}

function onDocumentClick(e) {
	const planAnchor = e.target.closest('a.js-property-plan-download');
	if (planAnchor) {
		handlePlanDownloadClick(planAnchor);
		return;
	}

	const anchor = e.target.closest('a.js-property-inquiry-link');
	if (!anchor) {
		return;
	}
	const href = anchor.getAttribute('href') || '';
	if (!href.startsWith('#')) {
		return;
	}
	const ref = readReferenceFromLink(anchor);
	storePropertyReference(ref);

	// Same-hash navigation does not fire hashchange; still prefill after scroll.
	window.requestAnimationFrame(() => {
		applyPendingPropertyReference();
	});
}

function onDomReady(fn) {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', fn, { once: true });
	} else {
		fn();
	}
}

function init() {
	// Capture phase: store property reference before modal-handler opens the overlay.
	document.addEventListener('click', onDocumentClick, true);

	onDomReady(() => {
		applyPendingPropertyReference();
	});

	window.addEventListener('hashchange', () => {
		applyPendingPropertyReference();
	});

	document.addEventListener('facetwp-loaded', () => {
		applyPendingPropertyReference();
	});

	document.addEventListener('regatta:clickdimensions-mount', (e) => {
		if (!getPendingReference()) {
			return;
		}

		const container = e.detail?.container;
		if (!container) {
			applyPendingPropertyReference();
			return;
		}

		const openModal = getOpenModalOverlay();
		const inOpenModal = Boolean(openModal?.contains(container));
		const inContact = Boolean(getContactSection()?.contains(container));
		const inPreload = Boolean(
			container.closest?.('.regatta-modal-preload-host, #regatta-modal-preload')
		);

		if (inPreload && !inOpenModal) {
			return;
		}
		if (openModal && !inOpenModal && !inContact) {
			return;
		}

		applyPendingPropertyReference();
	});

	document.addEventListener('modal:opened', () => {
		if (!getPendingReference()) {
			return;
		}
		applyPendingPropertyReference();
	});

	document.addEventListener('regatta:clickdimensions-iframe-ready', (e) => {
		if (!getPendingReference()) {
			return;
		}
		const iframe = e.detail?.iframe;
		const openModal = getOpenModalOverlay();
		if (iframe instanceof HTMLIFrameElement && openModal?.contains(iframe)) {
			applyPendingPropertyReference();
		}
	});
}

init();
